import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Shell } from "@/components/Shell";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Persona from "@/pages/Persona";
import Brand from "@/pages/Brand";
import Launch from "@/pages/Launch";
import Home from "@/pages/Home";
import Library from "@/pages/Library";
import GeneratePost from "@/pages/GeneratePost";
import ComposePost from "@/pages/ComposePost";
import Create from "@/pages/Create";
import CRM from "@/pages/CRM";
import Grow from "@/pages/Grow";
import ReleaseControl from "@/pages/ReleaseControl";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PublicSite from "@/pages/PublicSite";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="app-loading">
      <img src="/legacystack-mark.png" alt="" className="h-10 w-10 breathe" />
    </div>
  );
}

function Protected({ children, needsOrg = true }) {
  const { loading, isAuthenticated, org } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!isAuthenticated) return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  if (needsOrg && !org) return <Navigate to="/onboarding" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRouter() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/forgot" element={<ForgotPassword />} />
        <Route path="/auth/reset" element={<ResetPassword />} />
        <Route path="/s/:slug/*" element={<PublicSite />} />
        <Route path="/payment/success" element={<Protected needsOrg={false}><PaymentSuccess /></Protected>} />
        <Route path="/payment/cancel" element={<Protected needsOrg={false}><PaymentCancel /></Protected>} />
        <Route path="/admin/release" element={<Protected needsOrg={false}><ReleaseControl /></Protected>} />
        <Route path="/onboarding" element={<Protected needsOrg={false}><Onboarding /></Protected>} />
        <Route element={<Protected><Shell /></Protected>}>
          <Route path="/home" element={<Home />} />
          <Route path="/persona" element={<Persona />} />
          <Route path="/brand" element={<Brand />} />
          <Route path="/launch" element={<Launch />} />
          <Route path="/library" element={<Library />} />
          <Route path="/create" element={<Create />} />
          <Route path="/compose" element={<ComposePost />} />
          <Route path="/generate-post" element={<GeneratePost />} />
          <Route path="/grow" element={<Grow />} />
          <Route path="/crm" element={<CRM />} />
          <Route path="/billing" element={<Billing />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
